import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import {
  formatAiCreditsAmount,
  getDefaultAiCreditsRange,
} from '~src/utils/aiCreditsHistory';

export const getFuneralServiceLabel = (service = {}) => {
  const fullName = [service.cognome, service.nome].filter(Boolean).join(' ');

  return fullName || service.servizioName || service.objectId || '';
};

export const createDefaultFilters = () => ({
  preset: '30d',
  movementType: 'all',
  usedBy: 'all',
  funeralServiceId: '',
  funeralServiceName: '',
  ...getDefaultAiCreditsRange(),
});

export const formatShortDate = (dateValue) => {
  return format(parseISO(dateValue), 'dd/MM/yyyy');
};

export const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => ({
      type: 'page',
      value: index + 1,
    }));
  }

  if (currentPage <= 4) {
    return [
      { type: 'page', value: 1 },
      { type: 'page', value: 2 },
      { type: 'page', value: 3 },
      { type: 'page', value: 4 },
      { type: 'page', value: 5 },
      { type: 'ellipsis', value: 'end' },
      { type: 'page', value: totalPages },
    ];
  }

  if (currentPage >= totalPages - 3) {
    return [
      { type: 'page', value: 1 },
      { type: 'ellipsis', value: 'start' },
      { type: 'page', value: totalPages - 4 },
      { type: 'page', value: totalPages - 3 },
      { type: 'page', value: totalPages - 2 },
      { type: 'page', value: totalPages - 1 },
      { type: 'page', value: totalPages },
    ];
  }

  return [
    { type: 'page', value: 1 },
    { type: 'ellipsis', value: 'start' },
    { type: 'page', value: currentPage - 1 },
    { type: 'page', value: currentPage },
    { type: 'page', value: currentPage + 1 },
    { type: 'ellipsis', value: 'end' },
    { type: 'page', value: totalPages },
  ];
};

export const getBucketModeLabel = (bucketMode) => {
  if (bucketMode === 'day') {
    return 'giornaliera';
  }

  if (bucketMode === 'week') {
    return 'settimanale';
  }

  return 'mensile';
};

export const getPresetButtonLabel = (preset) => {
  if (preset.value === 'month' || preset.value === 'previousMonth') {
    return preset.label;
  }

  return `Ultimi ${preset.label}`;
};

export const formatPercentage = (value) => {
  return `${Math.round(value * 100)}%`;
};

const durationFormatter = new Intl.NumberFormat('it-IT', {
  maximumFractionDigits: 1,
});

export const formatUsageCount = (value) => {
  const label = Math.abs(value) === 1 ? 'volta' : 'volte';

  return `${formatAiCreditsAmount(value)} ${label}`;
};

const formatDurationLabel = (value, singular, plural) => {
  const roundedValue =
    value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;

  return `${durationFormatter.format(roundedValue)} ${
    roundedValue === 1 ? singular : plural
  }`;
};

export const formatDurationFromSeconds = (totalSeconds) => {
  if (totalSeconds < 60) {
    return formatDurationLabel(
      Math.max(0, Math.round(totalSeconds)),
      'secondo',
      'secondi'
    );
  }

  if (totalSeconds < 60 * 60) {
    return formatDurationLabel(totalSeconds / 60, 'minuto', 'minuti');
  }

  if (totalSeconds < 24 * 60 * 60) {
    return formatDurationLabel(totalSeconds / (60 * 60), 'ora', 'ore');
  }

  if (totalSeconds < 30 * 24 * 60 * 60) {
    return formatDurationLabel(
      totalSeconds / (24 * 60 * 60),
      'giorno',
      'giorni'
    );
  }

  if (totalSeconds < 365 * 24 * 60 * 60) {
    return formatDurationLabel(
      totalSeconds / (30 * 24 * 60 * 60),
      'mese',
      'mesi'
    );
  }

  return formatDurationLabel(
    totalSeconds / (365 * 24 * 60 * 60),
    'anno',
    'anni'
  );
};

export const formatDurationFromMinutes = (totalMinutes) => {
  return formatDurationFromSeconds(totalMinutes * 60);
};
